/**
 * Когда просмотр считается досмотренным — правило ОДНО на всех клиентов.
 *
 * Раньше его было два: своё в вебе (`packages/web/src/pages/watch-completion.ts`)
 * и своё на сервере. Пороги в них совпадали случайно, и разъехаться они могли в
 * любой момент — тогда один и тот же фильм выглядел бы досмотренным на
 * телевизоре и недосмотренным в браузере. Место такому правилу — в контрактах:
 * сервер применяет его один раз, клиенты получают готовый признак.
 *
 * Порог — доля хронометража, а не абсолютное время до конца. Прежде к доле
 * добавлялось условие «до конца меньше тридцати секунд», и оно решало всё:
 * зритель, ушедший на титрах за минуту до конца, оставался с недосмотренной
 * серией и вечной карточкой в «Продолжить». Титры идут по несколько минут, и
 * отмерять конец фильма секундами бессмысленно.
 */
/**
 * Какую долю надо посмотреть, чтобы просмотр считался завершённым.
 *
 * Восемьдесят пять процентов: к этому моменту рассказана вся история, а
 * оставшееся — финальная сцена и титры, которые досматривают не все. Прежние
 * девяносто процентов плюс тридцать секунд до конца требовали фактически
 * доиграть до самого края, и честно досмотренное кино оставалось в «Продолжить».
 */
export const WATCH_COMPLETION_FRACTION = 0.85;
/**
 * Сколько нужно посмотреть в любом случае, миллисекунды.
 *
 * Страховка от ВЫДУМАННОЙ длительности. У непрерывного потока ffmpeg своей
 * шкалы нет: пока сервер не сказал длительность источника, плеер берёт
 * `video.duration`, а тот в первые мгновения отдаёт разобранные из буфера
 * несколько секунд. Доля от такого числа берётся на второй секунде — и только
 * что включённая серия уезжала помеченной просмотренной. Полторы минуты
 * заведомо больше того, что успевает набежать до прихода настоящей
 * длительности, и заведомо меньше любой серии, которую и правда досмотрели.
 */
export const WATCH_COMPLETION_MIN_MS = 90_000;
/**
 * Правдоподобно ли «досмотрено» при такой позиции и длительности.
 *
 * Отвечает на вопрос «можно ли верить клиенту, сказавшему, что досмотрел», —
 * поэтому при неизвестной длительности отвечает ДА: у торрент-раздачи без
 * индекса длительности нет вовсе, а досмотреть её можно, и отказ сделал бы
 * такие раздачи вечно недосмотренными.
 */
export function watchCompletionAllowed(positionMs, durationMs) {
    if (!Number.isFinite(positionMs) || positionMs < 0)
        return false;
    if (durationMs === null || !Number.isFinite(durationMs) || durationMs <= 0)
        return true;
    // У конца обязано быть начало: иначе это старт с недоехавшей шкалой.
    if (positionMs < WATCH_COMPLETION_MIN_MS)
        return false;
    return positionMs / durationMs >= WATCH_COMPLETION_FRACTION;
}
/**
 * Досмотрен ли просмотр по одной лишь позиции, без слов клиента.
 *
 * Отвечает на другой вопрос — «дошли ли до конца сами», — и потому при
 * неизвестной длительности отвечает НЕТ: судить не о чем.
 *
 * Смотреть на позицию, а не только на присланный признак, обязательно:
 * приставка `completed` не присылает вовсе, и досмотренный на телевизоре фильм
 * без этой проверки навсегда оставался бы в «Продолжить».
 */
export function watchCompletedByPosition(positionMs, durationMs) {
    if (durationMs === null || !Number.isFinite(durationMs) || durationMs <= 0)
        return false;
    return watchCompletionAllowed(positionMs, durationMs);
}
//# sourceMappingURL=watch-completion.js.map